/**
 * Subscription Debouncer
 * Prevents excessive re-renders and state updates from rapid Firestore changes
 * by implementing intelligent debouncing with change detection.
 */

/**
 * @typedef {Object} DebounceConfig
 * @property {number} delay - Debounce delay in milliseconds
 * @property {number} maxWait - Maximum wait time before forcing an update
 * @property {boolean} leading - Execute on leading edge
 * @property {boolean} trailing - Execute on trailing edge
 * @property {Function} [changeDetector] - Custom change detection function
 * @property {Function} [keyExtractor] - Extract unique key from data for change detection
 */

/**
 * Default debounce configuration
 */
const DEFAULT_CONFIG = {
  delay: 150,           // 150ms debounce delay
  maxWait: 1000,        // 1 second max wait
  leading: false,       // Don't execute immediately
  trailing: true,       // Execute after delay
  changeDetector: null, // Will use default change detection
  keyExtractor: null    // Will use default key extraction
};

/**
 * Subscription debouncer class for managing multiple debounced subscriptions
 */
class SubscriptionDebouncer {
  constructor() {
    // Track active debounced functions
    this.debouncedFunctions = new Map();

    // Track last data for change detection
    this.lastDataByKey = new Map();

    // Track subscription metadata
    this.subscriptions = new Map();

    // Performance metrics
    this.metrics = {
      totalSubscriptions: 0,
      totalUpdates: 0,
      skippedUpdates: 0,
      averageDebounceTime: 0
    };
  }

  /**
   * Create a debounced subscription handler
   * @param {string} subscriptionKey - Unique key for this subscription
   * @param {Function} callback - Function to call when data changes
   * @param {DebounceConfig} config - Debounce configuration
   * @returns {Function} Debounced callback function
   */
  createDebouncedSubscription(subscriptionKey, callback, config = {}) {
    const finalConfig = { ...DEFAULT_CONFIG, ...config };

    console.log(`📊 Creating debounced subscription: ${subscriptionKey}`, {
      delay: finalConfig.delay,
      maxWait: finalConfig.maxWait
    });

    // Track subscription
    this.subscriptions.set(subscriptionKey, {
      key: subscriptionKey,
      config: finalConfig,
      callback,
      createdAt: new Date(),
      updateCount: 0,
      skippedCount: 0,
      lastUpdate: null
    });

    // Create debounced function with change detection
    const debouncedCallback = this.createDebouncer(
      (data) => {
        const subscription = this.subscriptions.get(subscriptionKey);
        if (!subscription) return;

        // Check if data actually changed
        if (this.hasDataChanged(subscriptionKey, data, finalConfig)) {
          console.log(`🔄 Subscription update: ${subscriptionKey}`, {
            dataLength: Array.isArray(data) ? data.length : 'N/A',
            updateCount: subscription.updateCount + 1
          });

          // Update metrics
          subscription.updateCount++;
          subscription.lastUpdate = new Date();
          this.metrics.totalUpdates++;

          // Store current data for next comparison
          this.storeLastData(subscriptionKey, data, finalConfig);

          // Execute callback
          callback(data);
        } else {
          console.log(`⏭️ Skipping duplicate update: ${subscriptionKey}`);
          subscription.skippedCount++;
          this.metrics.skippedUpdates++;
        }
      },
      finalConfig
    );

    // Store debounced function
    this.debouncedFunctions.set(subscriptionKey, debouncedCallback);
    this.metrics.totalSubscriptions++;

    return debouncedCallback;
  }

  /**
   * Check if data has actually changed since last update
   */
  hasDataChanged(subscriptionKey, newData, config) {
    const lastData = this.lastDataByKey.get(subscriptionKey);

    if (!lastData) {
      // First update, always proceed
      return true;
    }

    // Use custom change detector if provided
    if (config.changeDetector && typeof config.changeDetector === 'function') {
      return config.changeDetector(lastData, newData);
    }

    // Default change detection
    return this.defaultChangeDetection(lastData, newData, config);
  }

  /**
   * Default change detection logic
   */
  defaultChangeDetection(lastData, newData, config) {
    // Handle arrays (common for Firestore collections)
    if (Array.isArray(newData) && Array.isArray(lastData)) {
      // Check array length first (quick check)
      if (newData.length !== lastData.length) {
        return true;
      }

      // Check if key extractor is provided for more efficient comparison
      if (config.keyExtractor && typeof config.keyExtractor === 'function') {
        const newKeys = newData.map(config.keyExtractor).sort().join(',');
        const lastKeys = lastData.map(config.keyExtractor).sort().join(',');

        if (newKeys !== lastKeys) {
          return true;
        }

        // If keys are same, do deep comparison on objects
        return this.deepArrayComparison(lastData, newData, config.keyExtractor);
      }

      // Fallback to JSON comparison for arrays (less efficient but accurate)
      return JSON.stringify(newData) !== JSON.stringify(lastData);
    }

    // Handle objects
    if (typeof newData === 'object' && typeof lastData === 'object') {
      if (newData === null || lastData === null) {
        return newData !== lastData;
      }

      // Compare object keys first
      const newKeys = Object.keys(newData).sort();
      const lastKeys = Object.keys(lastData).sort();

      if (newKeys.length !== lastKeys.length) {
        return true;
      }

      if (newKeys.join(',') !== lastKeys.join(',')) {
        return true;
      }

      // Compare values
      return JSON.stringify(newData) !== JSON.stringify(lastData);
    }

    // Primitive comparison
    return newData !== lastData;
  }

  /**
   * Deep comparison of arrays using key extractor
   */
  deepArrayComparison(lastData, newData, keyExtractor) {
    // Create maps for efficient lookup
    const lastMap = new Map();
    const newMap = new Map();

    lastData.forEach(item => {
      const key = keyExtractor(item);
      lastMap.set(key, item);
    });

    newData.forEach(item => {
      const key = keyExtractor(item);
      newMap.set(key, item);
    });

    // Check if any items have changed
    for (const [key, newItem] of newMap) {
      const lastItem = lastMap.get(key);
      if (!lastItem || JSON.stringify(lastItem) !== JSON.stringify(newItem)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Store last data for comparison
   */
  storeLastData(subscriptionKey, data, config) {
    // Deep clone to prevent reference issues
    let clonedData;

    try {
      clonedData = JSON.parse(JSON.stringify(data));
    } catch (error) {
      console.warn(`Failed to clone data for subscription ${subscriptionKey}:`, error);
      clonedData = data; // Fallback to reference
    }

    this.lastDataByKey.set(subscriptionKey, clonedData);
  }

  /**
   * Create debouncer with leading/trailing edge support
   */
  createDebouncer(func, config) {
    let timeoutId = null;
    let maxTimeoutId = null;
    let lastCallTime = 0;
    let lastInvokeTime = 0;
    let leadingInvoked = false;

    const invokeFunc = (args) => {
      lastInvokeTime = Date.now();
      return func.apply(null, args);
    };

    const shouldInvoke = (time) => {
      const timeSinceLastCall = time - lastCallTime;
      const timeSinceLastInvoke = time - lastInvokeTime;

      return (
        lastCallTime === 0 ||
        timeSinceLastCall >= config.delay ||
        timeSinceLastCall < 0 ||
        (config.maxWait && timeSinceLastInvoke >= config.maxWait)
      );
    };

    const leadingEdge = (args) => {
      lastInvokeTime = lastCallTime;
      leadingInvoked = true;
      return invokeFunc(args);
    };

    const remainingWait = (time) => {
      const timeSinceLastCall = time - lastCallTime;
      const timeSinceLastInvoke = time - lastInvokeTime;
      const timeWaiting = config.delay - timeSinceLastCall;

      return config.maxWait
        ? Math.min(timeWaiting, config.maxWait - timeSinceLastInvoke)
        : timeWaiting;
    };

    const trailingEdge = (args) => {
      timeoutId = null;
      if (config.trailing && lastCallTime) {
        return invokeFunc(args);
      }
      lastCallTime = 0;
      leadingInvoked = false;
      return undefined;
    };

    const cancel = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (maxTimeoutId) {
        clearTimeout(maxTimeoutId);
        maxTimeoutId = null;
      }
      lastCallTime = 0;
      lastInvokeTime = 0;
      leadingInvoked = false;
    };

    const debounced = (...args) => {
      const time = Date.now();
      const isInvoking = shouldInvoke(time);

      lastCallTime = time;

      if (isInvoking) {
        if (timeoutId === null && config.leading && !leadingInvoked) {
          return leadingEdge(args);
        }

        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      }

      if (!timeoutId) {
        timeoutId = setTimeout(() => trailingEdge(args), config.delay);
      }

      // Set max wait timeout
      if (config.maxWait && !maxTimeoutId && timeoutId) {
        maxTimeoutId = setTimeout(() => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            trailingEdge(args);
          }
        }, config.maxWait);
      }
    };

    debounced.cancel = cancel;
    return debounced;
  }

  /**
   * Remove subscription and clean up
   */
  removeSubscription(subscriptionKey) {
    // Cancel debounced function
    const debouncedFn = this.debouncedFunctions.get(subscriptionKey);
    if (debouncedFn && debouncedFn.cancel) {
      debouncedFn.cancel();
    }

    // Clean up tracking
    this.debouncedFunctions.delete(subscriptionKey);
    this.lastDataByKey.delete(subscriptionKey);
    this.subscriptions.delete(subscriptionKey);

    console.log(`🗑️ Removed debounced subscription: ${subscriptionKey}`);
  }

  /**
   * Get subscription metrics and status
   */
  getMetrics() {
    const subscriptionDetails = Array.from(this.subscriptions.values()).map(sub => ({
      key: sub.key,
      updateCount: sub.updateCount,
      skippedCount: sub.skippedCount,
      lastUpdate: sub.lastUpdate,
      efficiency: sub.updateCount > 0 ? (sub.skippedCount / (sub.updateCount + sub.skippedCount)) * 100 : 0
    }));

    return {
      overview: this.metrics,
      subscriptions: subscriptionDetails,
      activeCount: this.subscriptions.size,
      totalEfficiency: this.metrics.totalUpdates > 0
        ? (this.metrics.skippedUpdates / (this.metrics.totalUpdates + this.metrics.skippedUpdates)) * 100
        : 0
    };
  }

  /**
   * Clear all subscriptions
   */
  clearAll() {
    for (const key of this.subscriptions.keys()) {
      this.removeSubscription(key);
    }

    // Reset metrics
    this.metrics = {
      totalSubscriptions: 0,
      totalUpdates: 0,
      skippedUpdates: 0,
      averageDebounceTime: 0
    };

    console.log('🧹 Cleared all debounced subscriptions');
  }
}

// Create singleton instance
const subscriptionDebouncer = new SubscriptionDebouncer();

// Firestore-specific debounce configurations
export const FIRESTORE_CONFIGS = {
  // For broker integrations (frequent status changes)
  BROKER_INTEGRATIONS: {
    delay: 100,
    maxWait: 500,
    keyExtractor: (item) => item.id || item.integrationId,
    changeDetector: (lastData, newData) => {
      // Custom logic for broker integration changes
      if (!Array.isArray(lastData) || !Array.isArray(newData)) {
        return true;
      }

      // Check for status changes, credential updates, or metadata changes
      if (lastData.length !== newData.length) return true;

      const lastMap = new Map(lastData.map(item => [item.id, item]));

      return newData.some(newItem => {
        const lastItem = lastMap.get(newItem.id);
        if (!lastItem) return true;

        // Check critical fields that should trigger updates
        return (
          lastItem.status !== newItem.status ||
          lastItem.updatedAt !== newItem.updatedAt ||
          JSON.stringify(lastItem.credentials?.updatedAt) !== JSON.stringify(newItem.credentials?.updatedAt) ||
          JSON.stringify(lastItem.metadata?.lastAccountSync) !== JSON.stringify(newItem.metadata?.lastAccountSync)
        );
      });
    }
  },

  // For user orders (high frequency updates)
  USER_ORDERS: {
    delay: 200,
    maxWait: 1000,
    keyExtractor: (item) => item.id || item.orderId,
    changeDetector: (lastData, newData) => {
      if (!Array.isArray(lastData) || !Array.isArray(newData)) {
        return true;
      }

      if (lastData.length !== newData.length) return true;

      const lastMap = new Map(lastData.map(item => [item.id, item]));

      return newData.some(newItem => {
        const lastItem = lastMap.get(newItem.id);
        if (!lastItem) return true;

        // Check order status, fill quantity, or updated timestamp
        return (
          lastItem.status !== newItem.status ||
          lastItem.filledQuantity !== newItem.filledQuantity ||
          lastItem.updatedAt !== newItem.updatedAt
        );
      });
    }
  },

  // For engagement data (less critical, can have longer delay)
  ENGAGEMENT_DATA: {
    delay: 300,
    maxWait: 2000,
    keyExtractor: (item) => item.id,
    changeDetector: (lastData, newData) => {
      // Simple length and content comparison for engagement data
      return JSON.stringify(lastData) !== JSON.stringify(newData);
    }
  },

  // For balance updates (moderate frequency)
  BALANCE_UPDATES: {
    delay: 150,
    maxWait: 800,
    keyExtractor: (item) => item.accountId || item.account_number,
    changeDetector: (lastData, newData) => {
      if (!lastData || !newData) return true;

      // Check for balance or equity changes
      return (
        lastData.cash_balance !== newData.cash_balance ||
        lastData.total_equity !== newData.total_equity ||
        lastData.buying_power !== newData.buying_power ||
        lastData.updatedAt !== newData.updatedAt
      );
    }
  }
};

// Export main functions
export {
  subscriptionDebouncer,
  SubscriptionDebouncer
};

/**
 * Create a debounced Firestore subscription
 * @param {string} subscriptionKey - Unique key for this subscription
 * @param {Function} callback - Callback to execute on data changes
 * @param {string} configType - Configuration type from FIRESTORE_CONFIGS
 * @param {Object} customConfig - Custom configuration overrides
 * @returns {Function} Debounced callback function
 */
export const createDebouncedFirestoreSubscription = (
  subscriptionKey,
  callback,
  configType = 'BROKER_INTEGRATIONS',
  customConfig = {}
) => {
  const baseConfig = FIRESTORE_CONFIGS[configType] || FIRESTORE_CONFIGS.BROKER_INTEGRATIONS;
  const finalConfig = { ...baseConfig, ...customConfig };

  return subscriptionDebouncer.createDebouncedSubscription(subscriptionKey, callback, finalConfig);
};

/**
 * Remove a debounced subscription
 */
export const removeDebouncedSubscription = (subscriptionKey) => {
  return subscriptionDebouncer.removeSubscription(subscriptionKey);
};

/**
 * Get debouncing metrics
 */
export const getSubscriptionMetrics = () => {
  return subscriptionDebouncer.getMetrics();
};

/**
 * Clear all debounced subscriptions
 */
export const clearAllSubscriptions = () => {
  return subscriptionDebouncer.clearAll();
};

// Debug helpers - only in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.subscriptionDebouncer = subscriptionDebouncer;
  window.getSubscriptionMetrics = getSubscriptionMetrics;
}

export default subscriptionDebouncer;